// Staged-ingest client (Session 19S §C-4 → Session 19AD).
//
// Bridges the frontend to the worker's POST /ingest/stream SSE endpoint.
// Yields IngestEvent values for the progress UI to consume.
//
// Session 19AD changes:
//   - Multi-source: accepts `slotFiles: Map<id, File>` from CourseDraft.sources.
//   - Browser-side parsing: each slot is parsed via @examforge/parser to
//     markdown BEFORE the network call. Worker no longer needs PDF parser
//     dependencies — keeps Cloudflare Worker lean and works for GLM/DeepSeek
//     (which can't ingest PDFs natively).
//   - Default flipped: VITE_USE_FIXTURE_INGEST defaults to '0' (real worker).
//     Set to '1' for in-browser fixture (used in tests / docs / demos).

import type { CourseDraft, CurriculumOutline, IngestPackage, LintReport, MasteryLevel, SourceSlot } from '@examforge/shared/schemas';
import { DEFAULT_MASTERY_LEVEL, INGEST_PACKAGE_SCHEMA_VERSION } from '@examforge/shared/schemas';
import {
  IngestPipelineError,
  IngestContractError,
  isIngestErrorCode,
  type IngestErrorCode,
} from '@examforge/shared/errors';
import { IngestStartResponseSchema, IngestStatusResponseSchema } from '@examforge/shared/api';
import type { IngestStatusResponse } from '@examforge/shared/api';
import { IngestEventSchema } from '@examforge/shared/schemas';
import { extract } from '@examforge/parser';
// 19BN Phase 0: reuse the centralized header builder (injects X-Anon-Id) from
// api-client so stream/start fetches carry the real anonId the backend reads.
import { ingestHeaders } from './api-client.js';

// Mirrors @examforge/ingest IngestEvent. We duplicate here rather than
// importing to keep frontend free of Hono/Workers deps.
//
// Session 19AE:
//   - Added `parse_files` browser-side pre-stage (clarifies that parsing
//     happens upstream of the SSE stream).
//   - `parse_past_papers` server stage renamed to `bundle_past_papers`
//     since the worker doesn't actually parse — it just packages the
//     already-parsed past_paper-kind slots.
export type StageId =
  | 'parse_files'              // browser-side: PDF/PPTX/DOCX → markdown
  | 'bundle_past_papers'       // server-side: package past_paper slots into IngestPackage.pastPapers[]
  | 'extract_cases'
  | 'flashcards'
  | 'mcqs'
  | 'true_false'
  | 'essays'
  | 'mindmaps'
  | 'glossary'
  | 'lecture_notes'
  | 'proof_drills'
  | 'free_responses'           // Session 19AN: short_answer / calculation / translation
  | 'reading_passages'         // Session 19AN: English reading + linked questions
  | 'mock_papers'
  | 'audit';                   // 19BI: Phase 4 deterministic audit (no LLM)

/** canonical ordered list. `condense_per_kind` is deliberately excluded —
 *  it's a server-side pre-stage (reorganizes multi-source before user-visible
 *  stages) and the frontend never renders it. */
export const STAGE_IDS: readonly StageId[] = [
  'parse_files',
  'bundle_past_papers', 'extract_cases', 'flashcards', 'mcqs', 'true_false',
  'essays', 'mindmaps', 'glossary', 'lecture_notes', 'proof_drills',
  'free_responses', 'reading_passages', 'mock_papers',
  'audit',
];

/** Stages that run on the server (the SSE stream). `parse_files` is browser-only. */
export const SERVER_STAGE_IDS: readonly StageId[] = STAGE_IDS.filter((s) => s !== 'parse_files');

export type IngestEvent =
  | { type: 'pipeline-start'; stages: readonly StageId[]; total: number }
  // G5: workflow-started carries the runId so cancelIngest can notify backend.
  | { type: 'workflow-started'; runId: string; instanceId: string }
  | { type: 'stage-start'; stage: StageId; index: number; total: number }
  | { type: 'stage-progress'; stage: StageId; done: number; total: number; note?: string }
  | { type: 'stage-skip'; stage: StageId; reason: string }
  | { type: 'stage-done'; stage: StageId; partial: Partial<IngestPackage> }
  | { type: 'stage-error'; stage: StageId; error: string; recoverable: boolean }
  // 19BD-PROD: terminal failure event from worker (orchestrator throw or
  // outer catch). Carries IngestErrorCode for frontend friendly-error mapping.
  // Reducer marks state with terminalError; client throws IngestPipelineError
  // after the stream end so course.tsx's catch surfaces [CODE] 描述 to UI.
  | { type: 'fatal'; code: IngestErrorCode; error: string }
  | {
      type: 'pipeline-complete';
      package: IngestPackage;
      meta: {
        totalDurationMs: number;
        providerName: string;
        recoveredStages: StageId[];
        /** Session 19AL · LLM_NATIVE §6.2: 8-char hash of each stage's prompt. */
        promptVersions?: Partial<Record<string, string>>;
        /** Session 19AL · LLM_NATIVE §5: deterministic post-pipeline lint. */
        lintReport?: LintReport;
        /** 19BI session 7: stages with significantly fewer items than manifest min. */
        lowYieldStages?: StageId[];
        /** Token usage from the pipeline (backend always returns these). */
        inputTokens?: number;
        outputTokens?: number;
        /** Per-stage duration breakdown (backend always returns this). */
        stageDurations?: Partial<Record<string, number>>;
        /** Which LLM stages were invoked. */
        llmStages?: StageId[];
        /** Model used per stage. */
        modelByStage?: Partial<Record<string, string>>;
        /** Phase 3.5 coordinator synthesis result. */
        coordinatorSynthesis?: unknown;
      };
    };

const INGEST_URL = (import.meta.env['VITE_INGEST_URL'] as string | undefined) ?? 'http://localhost:8787';
// Session 19AD: default flipped — real worker unless explicitly opted into fixture.
const USE_FIXTURE = (import.meta.env['VITE_USE_FIXTURE_INGEST'] as string | undefined) === '1';
// Session 19BE-PROD: default to Workflows path (POST /ingest/start + poll
// /ingest/status). Big courses bust the SSE-path 30s CPU cap; workflow gives
// each phase its own 30s bucket. Set VITE_INGEST_VIA=sse to fall back to the
// legacy single-invocation SSE pipeline.
const INGEST_VIA = (import.meta.env['VITE_INGEST_VIA'] as string | undefined) ?? 'workflow';

export interface StreamIngestOptions {
  draft: CourseDraft;
  /** id → File map for every slot in draft.sources. */
  slotFiles: ReadonlyMap<string, File>;
  signal?: AbortSignal;
  /** DEBT-19BI-EXTRACT-CONFIRM-STEP: frozen outline — if provided, extract_cases
   *  is skipped (Mode A) and the outline drives all downstream generation. */
  outline?: CurriculumOutline;
  /** 19BO+ 批次 C1: poll cadence override for the workflow path. Production
   *  defaults to POLL_INTERVAL_MS (5s). Exposed so tests can drive the poll loop
   *  without real sleeps, and so a future slower/faster cadence can be tuned
   *  without touching the client. Undefined → module default. */
  pollIntervalMs?: number;
}

export async function* streamIngestForDraft(
  opts: StreamIngestOptions,
): AsyncGenerator<IngestEvent, void, void> {
  if (USE_FIXTURE) {
    yield* simulateLocalIngest(opts);
    return;
  }
  if (INGEST_VIA === 'sse') {
    yield* fetchSSE(opts);
    return;
  }
  // Default: Workflows path
  yield* pollWorkflow(opts);
}

// ── Local fixture simulator (opt-in; smoke / doc / demo only) ───────────────

async function* simulateLocalIngest(
  opts: StreamIngestOptions,
): AsyncGenerator<IngestEvent, void, void> {
  const { draft } = opts;
  const startedAt = Date.now();
  const masteryLevel: MasteryLevel = draft.masteryLevel ?? DEFAULT_MASTERY_LEVEL;

  // Determine which stages would run for this draft (mirrors worker shouldRun).
  const enabled = new Set(draft.enabledComponents);
  const slots = draft.sources ?? [];
  const hasPastPapers = slots.some((s) => s.kind === 'past_paper');
  const hasMockPapers = (draft.mockPapers?.length ?? 0) > 0;

  const willRun: Record<StageId, boolean> = {
    parse_files: slots.length > 0,
    bundle_past_papers: hasPastPapers,
    extract_cases: true,
    flashcards: enabled.has('flashcards'),
    mcqs: enabled.has('quiz_mcq'),
    true_false: enabled.has('quiz_tf'),
    essays: enabled.has('essay'),
    mindmaps: enabled.has('mindmap'),
    glossary: enabled.has('glossary'),
    lecture_notes: enabled.has('lecture_notes'),
    proof_drills: enabled.has('proof_drill'),
    free_responses: enabled.has('free_response'),
    reading_passages: enabled.has('reading_passage'),
    mock_papers: enabled.has('mock_exam') && hasMockPapers,
    audit: true, // always runs — deterministic no-LLM pass
  };

  yield { type: 'pipeline-start', stages: STAGE_IDS, total: STAGE_IDS.length };

  for (let i = 0; i < STAGE_IDS.length; i++) {
    const stage = STAGE_IDS[i]!;
    if (!willRun[stage]) {
      yield { type: 'stage-skip', stage, reason: 'component_disabled_or_not_applicable' };
      continue;
    }
    yield { type: 'stage-start', stage, index: i, total: STAGE_IDS.length };
    const delay = stage === 'extract_cases' || stage === 'mock_papers' ? 350 : 180;
    await sleep(delay, opts.signal);
    if (opts.signal?.aborted) return;
    yield { type: 'stage-done', stage, partial: {} };
  }

  // Synthesize a minimal schema-valid package for the local-fixture path.
  // Fixture objects omit z.preprocess-injected fields (caseIds, topicIds, etc.)
  // — cast through unknown to IngestPackage since the fixture is synthetic.
  const pkg = {
    schemaVersion: INGEST_PACKAGE_SCHEMA_VERSION,
    courseId: draft.id,
    courseTitle: draft.title,
    cases: [
      {
        id: 'fixture_case_1',
        order: 1,
        title: draft.title || 'Topic 1',
        subtitle: 'Fixture stub (Session 19AD architecture mode)',
        color: '#1e3a5f',
        keyFrameworks: [],
        keyPlayers: [],
        keyYears: [],
        hasSubSections: false,
        subSections: null,
      },
    ],
    flashcards: enabled.has('flashcards')
      ? [
          {
            id: 'fixture_fc_01',
            caseId: 'fixture_case_1',
            section: null,
            type: 'fact',
            difficulty: 'easy',
            front: 'Architecture stub flashcard',
            back: '_Real LLM call pending — see TECH_DEBT §14._',
            tags: ['fixture'],
            relatedConcepts: [],
            relatedCards: [],
          },
        ]
      : [],
    mcqs: enabled.has('quiz_mcq')
      ? [
          {
            id: 'mcq_fixture_01',
            caseId: 'fixture_case_1',
            section: null,
            difficulty: 'easy',
            mockOnly: false,
            category: 'concept',
            question: 'Architecture stub MCQ',
            options: [
              'A) Option A',
              'B) Option B',
              'C) Option C',
              'D) Option D',
            ],
            correct: 0,
            explanation: '_Real LLM call pending — see TECH_DEBT §14._',
          },
        ]
      : [],
    ...(hasMockPapers && {
      mockExamItems: {
        papers: (draft.mockPapers ?? []).map((mp) => ({
          id: mp.id,
          style: mp.style,
          mcqs: [],
          trueFalse: [],
          essays: [],
        })),
      },
    }),
  } as unknown as IngestPackage;
  void masteryLevel;

  yield {
    type: 'pipeline-complete',
    package: pkg,
    meta: {
      totalDurationMs: Date.now() - startedAt,
      providerName: 'fixture',
      recoveredStages: [],
    },
  };
}

// ── Real SSE fetch (default in 19AD) ────────────────────────────────────────

interface ParsedSlotPayload {
  id: string;
  kind: SourceSlot['kind'];
  format: SourceSlot['format'];
  filename: string;
  /** Parsed markdown (browser-side via @examforge/parser). May be empty if parser fails. */
  markdown: string;
  /** Original byte length (informational; for cost / log purposes). */
  byteLength: number;
  label?: string;
  year?: number;
  priority?: number;
  customKindNote?: string;
  /** Non-fatal parser warnings, surfaced to the LLM via system prompt. */
  parseWarnings?: string[];
}

async function* fetchSSE(opts: StreamIngestOptions): AsyncGenerator<IngestEvent, void, void> {
  const { draft, slotFiles, signal } = opts;
  const slots = draft.sources ?? [];

  // ── pipeline-start (manually emit; mirrors worker semantics so the UI's
  //    progress reducer treats parse_files as the first stage). ──
  yield { type: 'pipeline-start', stages: STAGE_IDS, total: STAGE_IDS.length };

  // ── parse_files: browser-side extract (PDF/PPTX/DOCX → markdown) ──
  if (slots.length > 0) {
    yield { type: 'stage-start', stage: 'parse_files', index: 0, total: STAGE_IDS.length };
  } else {
    yield { type: 'stage-skip', stage: 'parse_files', reason: 'no_slots' };
  }

  // Parse each slot in browser. Failures degrade to empty markdown but don't
  // abort — the orchestrator's stage_error events handle downstream gaps.
  const parsedSlots: ParsedSlotPayload[] = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const file = slotFiles.get(slot.id);
    if (!file) continue;
    yield {
      type: 'stage-progress',
      stage: 'parse_files',
      done: i,
      total: slots.length,
      note: `${slot.kind} · ${slot.filename}`,
    };
    let markdown = '';
    let warnings: string[] | undefined;
    if (slot.format === 'pdf' || slot.format === 'pptx' || slot.format === 'docx') {
      try {
        const result = await extract(file);
        markdown = result.markdown;
        if (result.meta.warnings.length > 0) warnings = result.meta.warnings;
      } catch (e) {
        warnings = [`parse_failed: ${(e as Error).message}`];
      }
    } else if (slot.format === 'md' || slot.format === 'txt') {
      markdown = await file.text();
    }
    parsedSlots.push({
      id: slot.id,
      kind: slot.kind,
      format: slot.format,
      filename: slot.filename,
      markdown,
      byteLength: slot.byteLength,
      ...(slot.label && { label: slot.label }),
      ...(slot.year !== undefined && { year: slot.year }),
      ...(slot.priority !== undefined && { priority: slot.priority }),
      ...(slot.customKindNote && { customKindNote: slot.customKindNote }),
      ...(warnings && { parseWarnings: warnings }),
    });
  }
  if (slots.length > 0) {
    yield { type: 'stage-done', stage: 'parse_files', partial: {} };
  }

  // DEBT-19BI-EXTRACT-CONFIRM-STEP: if outline locked, skip extract_cases in UI.
  const sseSkipExtract = !!opts.outline;
  if (sseSkipExtract) {
    yield { type: 'stage-skip', stage: 'extract_cases', reason: 'outline_locked' };
  }

  const body = {
    courseId: draft.id,
    courseTitle: draft.title,
    enabledComponents: draft.enabledComponents,
    discipline: draft.discipline,
    pedagogyTypes: draft.pedagogyTypes,
    examFormat: draft.examFormat,
    masteryLevel: draft.masteryLevel,
    contentLanguage: draft.contentLanguage,
    mockPapers: draft.mockPapers,
    slots: parsedSlots,
    ...(draft.customDiscipline ? { customDiscipline: draft.customDiscipline } : {}),
    ...(draft.customPedagogy ? { customPedagogy: draft.customPedagogy } : {}),
    // 19BO+ P2-1: examType drives route_discipline scenario selection.
    ...(draft.examType ? { examType: draft.examType } : {}),
    ...(opts.outline ? { curriculumOutline: opts.outline } : {}),
  };

  const res = await fetch(`${INGEST_URL}/ingest/stream`, {
    method: 'POST',
    headers: ingestHeaders({ Accept: 'text/event-stream' }),
    body: JSON.stringify(body),
    ...(signal && { signal }),
  });
  if (!res.ok || !res.body) {
    // Session 19AE: capture worker's error body so 400 isn't a black box.
    // Worker returns { error, issues: [{ path, message }] } on validation failure.
    let detail = '';
    try {
      const text = await res.text();
      detail = text ? `\n${text.slice(0, 1000)}` : '';
      // 19BM-C: HTTP error body dump for dev diagnosis. Prod-reporting happens
      // via the thrown IngestApiError → reportApiError (19BL 3c). Guarded so
      // prod doesn't log raw response bodies.
      // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
      if (import.meta.env.DEV) console.error(`[staged-ingest-client] ${res.status} ${res.statusText}:`, text);
    } catch {
      // body already consumed or non-text — fall back to status only
    }
    throw new Error(`SSE failed: ${res.status} ${res.statusText}${detail}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // 19BD-PROD: track terminal disposition. If the stream ends without seeing
  // either pipeline-complete OR fatal, that's a CF wall-clock kill / socket
  // drop — surface as NETWORK_ABORT so the user gets a real code instead of
  // the generic "出了点问题" fallback.
  let sawCompletion = false;
  let pendingFatal: { code: IngestErrorCode; error: string } | null = null;
  // 19BO+ 批次 C1 P0-4 (FAILED-TERMINAL · TCP 半开防护): reader.read() 在 TCP
  // 真半开连接 (中间设备丢弃 FIN) 时永久 await,无任何事件 → 用户看到永久 streaming。
  // watchdog: 每次读取重置一个 5min 计时器,超时仍未收到数据 → 判 NETWORK_ABORT。
  // 5min 留足单 stage LLM 长等待余量 (worker 正常会在 stage 间发 keepalive/事件);
  // 真正静默 5min 几乎必定是连接已死。
  while (true) {
    const { value, done } = await readChunkWithWatchdog(reader, signal);
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIdx;
    while ((nlIdx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 2);
      const dataLine = block.split('\n').find((l) => l.startsWith('data: '));
      if (!dataLine) continue;
      try {
        // Phase 6: Zod safeParse replaces bare `as IngestEvent` cast.
        // On failure, warn + skip — a single malformed event should not
        // kill the SSE stream. pipeline-complete loss is caught by
        // /ingest/status polling fallback.
        const raw = JSON.parse(dataLine.slice(6));
        const eventParsed = IngestEventSchema.safeParse(raw);
        if (!eventParsed.success) {
          // eslint-disable-next-line no-console
          console.warn('[SSE] IngestEvent parse failed:', eventParsed.error.message);
          continue;
        }
        const ev = eventParsed.data as IngestEvent;
        // Suppress the worker's own pipeline-start — we already emitted ours
        // before parse_files. Worker stages then forward through unchanged.
        if (ev.type === 'pipeline-start') continue;
        // Suppress extract_cases events when outline is locked — we already
        // emitted stage-skip above. Worker Mode A sends stage-start/done but
        // the UI should show "skipped" not a second extraction cycle.
        if (sseSkipExtract
            && (ev.type === 'stage-start' || ev.type === 'stage-done' || ev.type === 'stage-progress')
            && 'stage' in ev && ev.stage === 'extract_cases') continue;
        if (ev.type === 'fatal') {
          // Stash so we can throw the structured error after the stream
          // closes; still yield it so the reducer can mark the terminal state.
          const code = isIngestErrorCode(ev.code) ? ev.code : 'UNKNOWN';
          pendingFatal = { code, error: ev.error };
        } else if (ev.type === 'pipeline-complete') {
          sawCompletion = true;
        }
        yield ev;
      } catch (sseErr) {
        // Skip malformed events; SSE protocol allows partial chunks.
        // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
        if (import.meta.env.DEV) console.warn('[staged-ingest-client] malformed SSE event:', sseErr);
      }
    }
  }

  // Stream ended. Decide what to throw (if anything):
  if (pendingFatal) {
    throw new IngestPipelineError(pendingFatal.code, pendingFatal.error);
  }
  if (!sawCompletion) {
    throw new IngestPipelineError(
      'NETWORK_ABORT',
      'connection ended before completion (likely worker wall-clock kill or network drop)',
    );
  }
}

// ── Workflow path (default 19BE-PROD) ───────────────────────────────────────
//
// POST /ingest/start → poll /ingest/status?runId=X every 5s.
// Each phase boundary in the polled snapshot is synthesized into SSE-style
// stage-start/stage-done events so the existing IngestProgress reducer can
// stay unchanged. Granularity is phase-level (Phase 3's 9 sub-stages all
// flip at once), but that matches what the workflow itself reports.

// Maps server-side phase to the frontend's StageId list. Note `condense_per_kind`
// and `qa_fact_check` are worker-internal stages not in the frontend StageId
// enum (see line 28). User sees Phase 1 progress via `bundle_past_papers`; the
// condense phase runs in parallel but isn't shown separately.
const PHASE_TO_STAGES: Record<string, StageId[]> = {
  phase1: ['bundle_past_papers'],
  phase2: ['extract_cases'],
  phase3: ['flashcards', 'mcqs', 'true_false', 'essays', 'mindmaps', 'glossary', 'lecture_notes', 'proof_drills', 'free_responses', 'reading_passages', 'mock_papers'],
  phase4: ['audit'],
  finalize: [],
};
const PHASE_ORDER: readonly string[] = ['phase1', 'phase2', 'phase3', 'phase4', 'finalize'];

async function* pollWorkflow(opts: StreamIngestOptions): AsyncGenerator<IngestEvent, void, void> {
  const { draft, slotFiles, signal } = opts;
  const slots = draft.sources ?? [];

  // ── pipeline-start + browser-side parse_files (mirrors fetchSSE) ──
  yield { type: 'pipeline-start', stages: STAGE_IDS, total: STAGE_IDS.length };
  // Pre-allocate tracking sets (used for outline-locked skip + poll loop).
  const emittedStarts = new Set<StageId>();
  const emittedDones = new Set<StageId>();
  if (slots.length > 0) {
    yield { type: 'stage-start', stage: 'parse_files', index: 0, total: STAGE_IDS.length };
  } else {
    yield { type: 'stage-skip', stage: 'parse_files', reason: 'no_slots' };
  }
  const parsedSlots: ParsedSlotPayload[] = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const file = slotFiles.get(slot.id);
    if (!file) continue;
    yield {
      type: 'stage-progress',
      stage: 'parse_files',
      done: i,
      total: slots.length,
      note: `${slot.kind} · ${slot.filename}`,
    };
    let markdown = '';
    let warnings: string[] | undefined;
    if (slot.format === 'pdf' || slot.format === 'pptx' || slot.format === 'docx') {
      try {
        const result = await extract(file);
        markdown = result.markdown;
        if (result.meta.warnings.length > 0) warnings = result.meta.warnings;
      } catch (e) {
        warnings = [`parse_failed: ${(e as Error).message}`];
      }
    } else if (slot.format === 'md' || slot.format === 'txt') {
      markdown = await file.text();
    }
    parsedSlots.push({
      id: slot.id,
      kind: slot.kind,
      format: slot.format,
      filename: slot.filename,
      markdown,
      byteLength: slot.byteLength,
      ...(slot.label && { label: slot.label }),
      ...(slot.year !== undefined && { year: slot.year }),
      ...(slot.priority !== undefined && { priority: slot.priority }),
      ...(slot.customKindNote && { customKindNote: slot.customKindNote }),
      ...(warnings && { parseWarnings: warnings }),
    });
  }
  if (slots.length > 0) {
    yield { type: 'stage-done', stage: 'parse_files', partial: {} };
  }

  // ── DEBT-19BI-EXTRACT-CONFIRM-STEP: if outline was already confirmed,
  //    extract_cases runs in Mode A (zero-LLM derivation from outline).
  //    Emit stage-skip immediately so the UI shows "已跳过 (大纲已锁定)"
  //    instead of a confusing second extraction cycle. ──
  if (opts.outline) {
    yield { type: 'stage-skip', stage: 'extract_cases', reason: 'outline_locked' };
    emittedStarts.add('extract_cases');
    emittedDones.add('extract_cases');
  }

  // ── POST /ingest/start ─────────────────────────────────────────────
  const body = {
    courseId: draft.id,
    courseTitle: draft.title,
    enabledComponents: draft.enabledComponents,
    discipline: draft.discipline,
    pedagogyTypes: draft.pedagogyTypes,
    examFormat: draft.examFormat,
    masteryLevel: draft.masteryLevel,
    contentLanguage: draft.contentLanguage,
    mockPapers: draft.mockPapers,
    slots: parsedSlots,
    ...(draft.customDiscipline ? { customDiscipline: draft.customDiscipline } : {}),
    ...(draft.customPedagogy ? { customPedagogy: draft.customPedagogy } : {}),
    // 19BO+ P2-1: examType drives route_discipline scenario selection.
    ...(draft.examType ? { examType: draft.examType } : {}),
    ...(opts.outline ? { curriculumOutline: opts.outline } : {}),
  };
  const startRes = await fetch(`${INGEST_URL}/ingest/start`, {
    method: 'POST',
    headers: ingestHeaders(),
    body: JSON.stringify(body),
    ...(signal && { signal }),
  });
  if (!startRes.ok) {
    let detail = '';
    try {
      const text = await startRes.text();
      detail = text ? `\n${text.slice(0, 1000)}` : '';
      // 19BM-C: HTTP error body dump for dev diagnosis. Guarded — prod
      // reporting via thrown IngestApiError → reportApiError.
      // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
      if (import.meta.env.DEV) console.error(`[poll-workflow] /ingest/start ${startRes.status}:`, text);
    } catch {
      // body already consumed
    }
    throw new IngestPipelineError(
      'PROVIDER_INIT_FAILED',
      `workflow start failed: ${startRes.status} ${startRes.statusText}${detail}`,
    );
  }
  const startRaw = await startRes.json();
  const startParsed = IngestStartResponseSchema.safeParse(startRaw);
  if (!startParsed.success) {
    throw new IngestContractError(startRes.status, startParsed.error, startRaw);
  }
  const startResp = startParsed.data;
  // G5: emit workflow-started so course.tsx can capture the runId for cancel.
  yield { type: 'workflow-started', runId: startResp.runId, instanceId: startResp.instanceId };

  // ── Poll loop (5 s cadence · overridable via opts.pollIntervalMs for tests) ──
  const POLL_INTERVAL_MS = opts.pollIntervalMs ?? 5_000;
  // 19BO+ 批次 C1: 若 status='started' 但 lastCheckpointAt 超 此时长未动,
  // 判定 worker wall-clock kill (markFailed 没跑成,error_code 永不写入)。
  // 5min: CF Workflow step 上限 60min,单 phase 正常推进间隔远小于此;
  // 卡死超 5min 几乎必定是 isolate 被杀。详见上方 wall-clock 分支注释。
  const POLL_STALE_THRESHOLD_MS = 5 * 60 * 1000;

  for (;;) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    await sleep(POLL_INTERVAL_MS, signal);
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');

    let snap: IngestStatusResponse;
    try {
      const r = await fetch(
        `${INGEST_URL}/ingest/status?runId=${encodeURIComponent(startResp.runId)}`,
        signal ? { signal } : {},
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const snapRaw = await r.json();
      const snapParsed = IngestStatusResponseSchema.safeParse(snapRaw);
      if (!snapParsed.success) {
        throw new IngestContractError(r.status, snapParsed.error, snapRaw);
      }
      snap = snapParsed.data as IngestStatusResponse;
    } catch (err) {
      // Transient network blip — keep polling. Real terminal failures are
      // surfaced via snap.errorCode below; only the user-cancel path throws.
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      // eslint-disable-next-line no-console
      console.warn('[poll-workflow] /ingest/status fetch hiccup (retrying):', (err as Error).message);
      continue;
    }

    // Synthesize stage-start/done events from currentPhase.
    const currentPhase = snap.currentPhase ?? 'phase1';
    const currentIdx = PHASE_ORDER.indexOf(currentPhase);
    if (currentIdx >= 0) {
      // All stages in phases strictly before currentPhase → done.
      for (let i = 0; i < currentIdx; i++) {
        const phaseId = PHASE_ORDER[i]!;
        for (const stageId of PHASE_TO_STAGES[phaseId] ?? []) {
          if (!emittedStarts.has(stageId)) {
            yield { type: 'stage-start', stage: stageId, index: 0, total: STAGE_IDS.length };
            emittedStarts.add(stageId);
          }
          if (!emittedDones.has(stageId)) {
            yield { type: 'stage-done', stage: stageId, partial: {} };
            emittedDones.add(stageId);
          }
        }
      }
      // currentPhase stages → mark started (running).
      for (const stageId of PHASE_TO_STAGES[currentPhase] ?? []) {
        if (!emittedStarts.has(stageId)) {
          yield { type: 'stage-start', stage: stageId, index: 0, total: STAGE_IDS.length };
          emittedStarts.add(stageId);
        }
      }
    }

    // Terminal: error code set on D1 row.
    if (snap.errorCode) {
      const code: IngestErrorCode = isIngestErrorCode(snap.errorCode) ? snap.errorCode : 'UNKNOWN';
      const msg = snap.errorMessage ?? snap.errorCode;
      yield { type: 'fatal', code, error: msg };
      throw new IngestPipelineError(code, msg);
    }

    // 19BO+ 批次 C1 P0-5 (POLL-ABORTED-INFINITE-LOOP): aborted/failed 状态必须终止。
    // 原代码注释 "status='started' or 'aborted' — keep polling" 是确定 bug ——
    // stale-lock cron (lock.ts cleanupStaleLocks) 每 10min 把超 4h 的 started 行
    // 标成 'aborted' 但**不写 error_code**。前端不处理 aborted = 任何卡住的 run
    // (worker 崩溃 / CF workflow 被终止 / markFailed 没跑成) 4h 后必然进入永久死循环,
    // spinner 永转,用户只能手动关标签页。aborted 是终态,绝不继续轮询。
    if (snap.status === 'aborted' || snap.status === 'failed') {
      const code: IngestErrorCode = 'WORKER_WALL_CLOCK';
      const msg = snap.errorMessage ?? `run ${snap.status} without error code`;
      yield { type: 'fatal', code, error: msg };
      throw new IngestPipelineError(code, msg);
    }

    // 19BO+ 批次 C1 P0-4 (FAILED-TERMINAL · wall-clock kill): status 仍 'started'
    // 但 lastCheckpointAt 超 POLL_STALE_THRESHOLD_MS 未动 → CF 杀了 worker isolate,
    // markFailed 没跑成,error_code 永不写入。复用后端已返回的 lastCheckpointAt
    // (v4.ts IngestStatusResponseSchema),前端首次消费此信号检测静默崩溃。
    // 阈值 5min: 对齐审计建议 + 留足 CF Workflow step 60min 超时内的合理窗口。
    if (snap.status === 'started' && snap.lastCheckpointAt !== null) {
      const staleMs = Date.now() - snap.lastCheckpointAt;
      if (staleMs > POLL_STALE_THRESHOLD_MS) {
        const msg = `no progress for ${Math.round(staleMs / 60_000)}min (likely worker wall-clock kill)`;
        yield { type: 'fatal', code: 'WORKER_WALL_CLOCK', error: msg };
        throw new IngestPipelineError('WORKER_WALL_CLOCK', msg);
      }
    }

    // Terminal: success — emit any not-yet-done stages + pipeline-complete.
    if (snap.status === 'completed' && snap.package) {
      for (const phaseId of PHASE_ORDER) {
        for (const stageId of PHASE_TO_STAGES[phaseId] ?? []) {
          if (!emittedStarts.has(stageId)) {
            yield { type: 'stage-start', stage: stageId, index: 0, total: STAGE_IDS.length };
            emittedStarts.add(stageId);
          }
          if (!emittedDones.has(stageId)) {
            yield { type: 'stage-done', stage: stageId, partial: {} };
            emittedDones.add(stageId);
          }
        }
      }
      yield {
        type: 'pipeline-complete',
        // package from /ingest/status validated as StatusResult (Phase 5b).
        // lintReport now typed as LintReport | null (Session 39 z.unknown hardening).
        package: snap.package as IngestPackage,
        meta: {
          totalDurationMs: snap.durationMs,
          providerName: 'workflow',
          recoveredStages: [],
          ...(snap.lintReport ? { lintReport: snap.lintReport } : {}),
        },
      };
      return;
    }
    // status='started' 且 checkpoint 未超时 — 继续轮询。(CF Workflow 大课程单
    // phase 可能数分钟;aborted/failed/wall-clock 已在上面的终态分支处理。)
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * 19BO+ 批次 C1: read one chunk from a SSE reader, aborting with NETWORK_ABORT
 * if no data arrives within WATCHDOG_MS. Guards against TCP half-open connections
 * (intermediate proxies dropping FIN) where reader.read() would otherwise await
 * forever, leaving the UI stuck on a perpetual "streaming" spinner.
 *
 * The timer is per-read: each successful chunk resets it. A genuine long LLM
 * stage still emits stage-progress / keepalive events within the window; a silent
 * WATCHDOG_MS gap almost certainly means the connection is dead.
 *
 * On timeout we throw IngestPipelineError('NETWORK_ABORT') which the existing
 * friendly-error chain surfaces as "[NETWORK_ABORT] 连接中断".
 */
const SSE_WATCHDOG_MS = 5 * 60 * 1000;

async function readChunkWithWatchdog(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  signal?: AbortSignal,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  // Exported (via the named export below) so the watchdog logic can be unit-
  // tested in isolation — exercising it through fetchSSE would require an SSE
  // ReadableStream mock + fake-timer wiring disproportionate to the logic's size.
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new IngestPipelineError(
        'NETWORK_ABORT',
        `SSE watchdog: no data for ${Math.round(SSE_WATCHDOG_MS / 60_000)}min (likely half-open connection)`,
      ));
    }, SSE_WATCHDOG_MS);

    reader.read().then(
      (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(err);
      },
    );

    // If the caller aborts (user cancel / navigation), reject promptly so the
    // watchdog timer doesn't keep the event loop alive.
    signal?.addEventListener('abort', () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    }, { once: true });
  });
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    }, { once: true });
  });
}

// 19BO+ 批次 C1: exposed for unit testing the watchdog in isolation.
export { readChunkWithWatchdog as _readChunkWithWatchdog };
